import { translateError } from "@/lib/errors";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Type } from "lucide-react";
import { Button } from "@/components/primitives/Button";
import { Card } from "@/components/primitives/Card";
import { TextDialog } from "@/components/montage/TextDialog";
import { addText, deleteText, fmtDuration, patchText, type Montage, type MontageText } from "@/lib/montageApi";

const errMsg = (err: any, fallback: string) => translateError(err?.response?.data?.detail) || fallback;

interface Props {
  m: Montage;
  onUpdate: (m: Montage) => void;
  disabled: boolean;
}

export function TextPanel({ m, onUpdate, disabled }: Props) {
  const { t } = useTranslation();
  const texts = m.texts ?? [];
  const [dialog, setDialog] = useState<null | { edit: MontageText | null }>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const noVideo = m.items.length === 0;

  const remove = async (tx: MontageText) => {
    if (!window.confirm(t("textOverlay.confirmRemove"))) return;
    setBusy(tx.id);
    try {
      onUpdate(await deleteText(m.id, tx.id));
      toast.success(t("textOverlay.removed"));
    } catch (err) {
      toast.error(errMsg(err, t("textOverlay.removeFailed")));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card data-testid="montage-text-panel" className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="font-display text-sm font-semibold uppercase tracking-wide">
          {t("textOverlay.title")} <span data-testid="montage-text-count" className="text-[#7FD4FF]">{texts.length > 0 ? `· ${texts.length}` : ""}</span>
        </p>
        <Button data-testid="montage-text-add-btn" variant="secondary" size="sm" disabled={disabled || noVideo} onClick={() => setDialog({ edit: null })}>
          <Plus className="h-4 w-4" /> {t("textOverlay.add")}
        </Button>
      </div>
      {texts.length === 0 ? (
        <p data-testid="montage-text-empty" className="text-xs text-muted-foreground">{noVideo ? t("textOverlay.addVideoFirst") : t("textOverlay.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {texts.map((tx, i) => (
            <li key={tx.id} data-testid={`montage-text-row-${i}`} className="glass-panel flex items-center gap-3 rounded-xl p-3">
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md border border-[#2E7BFF]/50 bg-[#2E7BFF]/15 text-[#7FD4FF]"><Type className="h-3.5 w-3.5" /></span>
              <div className="min-w-0 flex-1 text-xs">
                <p data-testid={`montage-text-${i}-content`} className="truncate text-sm text-foreground">{tx.text}</p>
                <p data-testid={`montage-text-${i}-meta`} className="text-muted-foreground">
                  {t(`textOverlay.${tx.position}`)} · {t(`textOverlay.${tx.size}`)} · {t("textOverlay.visibleRange", { from: fmtDuration(tx.start), to: fmtDuration(tx.end) })}
                </p>
              </div>
              <button data-testid={`montage-text-${i}-edit`} type="button" disabled={disabled || busy !== null} onClick={() => setDialog({ edit: tx })} title={t("common.edit")} className="icon-action">
                <Pencil className="h-4 w-4" />
              </button>
              <button data-testid={`montage-text-${i}-remove`} type="button" disabled={disabled || busy !== null} onClick={() => remove(tx)} title={t("common.remove")} className="icon-action icon-action-danger">
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {dialog && createPortal(
        <TextDialog
          m={m}
          initial={dialog.edit}
          onClose={() => setDialog(null)}
          onSave={async (body) => {
            try {
              const updated = dialog.edit ? await patchText(m.id, dialog.edit.id, body) : await addText(m.id, body);
              setDialog(null);
              onUpdate(updated);
              toast.success(dialog.edit ? t("textOverlay.saved") : t("textOverlay.added"));
            } catch (err) {
              toast.error(errMsg(err, t("textOverlay.notSaved")));
              throw err;
            }
          }}
        />,
        document.body,
      )}
    </Card>
  );
}
